/**
 * Firebase Configuration for Finding Your Voice
 *
 * Supports dual Firebase projects:
 * 1. Finding Your Voice project — LEGACY per-user storage (read-only since
 *    the hub thin-layer conversion; see src/services/hubAppState.js)
 * 2. Auth-Hub project — SSO across DDD apps AND the home of per-user app
 *    state (users/{uid}/apps/finding-your-voice via getAppState/saveAppState)
 *
 * The two apps initialise independently: a build with only the hub key still
 * gets hub SSO and hub sync, because this app's own Firebase project may not
 * be configured at all. The exported onAuthStateChanged observes BOTH
 * sessions — the own-project session wins when both exist, and the hub
 * session alone still counts as signed in (the hub does not mint a spoke
 * token for finding-your-voice, so a real SSO flow produces only a hub
 * session).
 */

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  signInWithEmailAndPassword as firebaseSignInWithEmail,
  createUserWithEmailAndPassword as firebaseCreateUserWithEmail,
  signOut as firebaseSignOut,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail as firebaseSendPasswordReset,
  updateProfile as firebaseUpdateProfile,
} from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';

export const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const authHubConfig = {
  apiKey: import.meta.env.VITE_AUTH_HUB_API_KEY,
  authDomain: 'ddd-auth-hub-4ed14.firebaseapp.com',
  projectId: 'ddd-auth-hub-4ed14',
};

const useAuthHub = import.meta.env.VITE_USE_AUTH_HUB === 'true';

let app = null;
let authHubApp = null;
let auth = null;
let authHubAuth = null;
let db = null;

if (!isDemoMode && firebaseConfig.apiKey) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);

    enableIndexedDbPersistence(db).catch((err) => {
      if (err.code === 'failed-precondition') {
        console.warn('Firestore persistence unavailable: multiple tabs open');
      } else if (err.code === 'unimplemented') {
        console.warn('Firestore persistence unavailable: browser not supported');
      }
    });
  } catch (error) {
    console.error('Firebase initialisation error:', error);
  }
}

// Hub app is independent of the own-project config — hub SSO and the
// thin-layer sync must work even when this app's own project is absent.
if (!isDemoMode && useAuthHub && authHubConfig.apiKey) {
  try {
    authHubApp = initializeApp(authHubConfig, 'auth-hub');
    authHubAuth = getAuth(authHubApp);
  } catch (error) {
    console.error('Auth hub initialisation error:', error);
  }
}

/** The signed-in Firebase user from whichever session exists (own wins). */
export const currentAuthUser = () =>
  auth?.currentUser || authHubAuth?.currentUser || null;

export const onAuthStateChanged = (callback) => {
  if (isDemoMode || (!auth && !authHubAuth)) return () => {};
  let ownUser = auth?.currentUser || null;
  let hubUser = authHubAuth?.currentUser || null;
  const emit = () => callback(ownUser || hubUser);
  const unsubscribes = [];
  if (auth) {
    unsubscribes.push(firebaseOnAuthStateChanged(auth, (user) => {
      ownUser = user;
      emit();
    }));
  }
  if (authHubAuth) {
    unsubscribes.push(firebaseOnAuthStateChanged(authHubAuth, (user) => {
      hubUser = user;
      emit();
    }));
  }
  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
};

export const signInWithEmailAndPassword = async (email, password) => {
  if (isDemoMode || !auth) throw new Error('Firebase not configured');
  return firebaseSignInWithEmail(auth, email, password);
};

export const createUserWithEmailAndPassword = async (email, password) => {
  if (isDemoMode || !auth) throw new Error('Firebase not configured');
  return firebaseCreateUserWithEmail(auth, email, password);
};

export const signOut = async () => {
  if (isDemoMode) return;
  // Hub first, own second: the combined listener then emits exactly one
  // final null instead of flickering through a hub-only user.
  if (authHubAuth) {
    await firebaseSignOut(authHubAuth).catch(() => {});
  }
  if (auth) {
    await firebaseSignOut(auth);
  }
};

export const signInWithGoogle = async () => {
  if (isDemoMode || !auth) throw new Error('Firebase not configured');
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider);
};

export const sendPasswordResetEmail = async (email) => {
  if (isDemoMode || !auth) throw new Error('Firebase not configured');
  return firebaseSendPasswordReset(auth, email);
};

export const updateProfile = async (user, profile) => {
  if (isDemoMode || (!auth && !authHubAuth)) return;
  return firebaseUpdateProfile(user, profile);
};

export { app, auth, authHubApp, authHubAuth, db, useAuthHub };
